/**
 * useGridPicture — memoized grid + plan-surface picture. Re-records only on
 * plan size / grid size / grid toggle / theme change — never on object
 * edits or camera moves.
 *
 * @module floorplan/hooks/useGridPicture
 * @category FloorPlan
 */

import { useMemo } from 'react';
import type { SkPicture } from '@shopify/react-native-skia';

import { recordGridPicture } from '../renderer/recordPicture';
import type { FloorTheme } from '../renderer/types';
import { useFloorEngineStore } from '../stores/useFloorEngineStore';

const useGridPicture = (theme: FloorTheme): SkPicture | null => {
  const planWidth = useFloorEngineStore((s) => s.meta?.width ?? 0);
  const planHeight = useFloorEngineStore((s) => s.meta?.height ?? 0);
  const gridSize = useFloorEngineStore((s) => s.meta?.grid?.size ?? 0);
  const gridEnabled = useFloorEngineStore((s) => s.gridEnabled);

  return useMemo(() => {
    if (planWidth <= 0 || planHeight <= 0) return null;
    return recordGridPicture(
      planWidth,
      planHeight,
      gridEnabled ? gridSize : 0,
      { surface: theme.surface, grid: theme.grid },
    );
  }, [planWidth, planHeight, gridSize, gridEnabled, theme]);
};

export default useGridPicture;
